/**
 * TripChipper (TravelArrow) - Travel Better — travel deals / flight overlay
 *
 * Declares a single web-accessible-resource entry matched to every origin with no
 * `use_dynamic_url`, covering named stylesheets, `assets/**` (recursive), `popup.html`
 * and several `legroom/` and `carrental/` scripts. All are reachable from any page at
 * a fixed URL.
 *
 * The vendor logo is the cleanest signal: it lives under the exposed `assets/` tree and
 * loads through `probeImage` with no CORS grant, so it survives a restrictive page CSP.
 *
 * Manifest verified against version 10.0.9.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'coplmfnphahpcknbchcehdikbdieognn';

export const tripchipper: ExtensionDefinition = {
  id: ID,
  name: 'TripChipper (TravelArrow) - Travel Better',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 200_000,
  notes:
    'assets/**, named stylesheets and scripts exposed to every origin with no dynamic ' +
    'URL. The vendor logo loads without a CORS grant, so detection is conclusive.',
  signals: [
    {
      id: 'war-logo-image',
      kind: 'web-accessible-resource',
      description:
        'Load assets/img/logo.webp via <img> — the vendor logo, exposed to every origin ' +
        'and readable without a CORS grant.',
      weight: 1,
      run: () => probeImage(ID, 'assets/img/logo.webp'),
    },
    {
      id: 'war-app-css',
      kind: 'web-accessible-resource',
      description: 'Fetch styles/app.css — a named stylesheet exposed to every origin.',
      weight: 0.5,
      run: () => probeFetch(ID, 'styles/app.css'),
    },
    {
      id: 'war-load-flight-data-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch legroom/load_flight_data.js — a vendor-named script, a third independent path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'legroom/load_flight_data.js'),
    },
  ],
};
